import { useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errorHandler';
import useDebounce from '../../utils/debounce';
import WarningMessage from '../../components/WarningMessage';
import {
  useCreateFactory,
  useDeleteFactory,
  useFetchFactories,
  useUpdateFactory,
} from './Hooks';

const { Title } = Typography;

export default function FactoriesPage() {
  const { t } = useTranslation('factory');
  const { user } = useAuthStore();
  const { operatorId } = useOperatorStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const isReadOnly = user?.role === 'READ_ONLY_ADMIN';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form] = Form.useForm();

  const { data, isLoading, isFetching, refetch } = useFetchFactories({
    operatorId,
    page,
    search: debouncedSearch,
    limit: pageSize,
  });

  const { mutate: createFactory, isPending: creating } = useCreateFactory({
    onSuccess: () => {
      message.success(t('createSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: updateFactory, isPending: updating } = useUpdateFactory({
    onSuccess: () => {
      message.success(t('updateSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: deleteFactory, isPending: deleting } = useDeleteFactory({
    onSuccess: () => {
      message.success(t('deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const factories = data?.paginatedData || [];
  const totalCount = data?.totalCount || 0;

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      number: record.number,
      name: record.name || '',
    });
    setDrawerOpen(true);
  };

  const onFinish = (values) => {
    const payload = {
      operatorId,
      number: values.number?.trim(),
      name: values.name?.trim() || undefined,
    };
    if (editing) {
      updateFactory({ id: editing._id, ...payload });
      return;
    }
    createFactory(payload);
  };

  const columns = [
    {
      title: t('number'),
      dataIndex: 'number',
      key: 'number',
      align: 'center',
    },
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) =>
        isEditAllowed && !isReadOnly ? (
          <div className="flex justify-center gap-2">
            <Tooltip title={t('edit')}>
              <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
            <Tooltip title={t('delete')}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setDeleteTarget(record)}
              />
            </Tooltip>
          </div>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                allowClear
                placeholder={t('searchPlaceholder')}
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (page !== 1) setPage(1);
                }}
              />
              <Tooltip title={t('refresh')}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                />
              </Tooltip>
            </div>
            {isEditAllowed && !isReadOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t('add')}
              </Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={factories}
            loading={isLoading || isFetching}
            rowKey={(record) => record._id}
            size="small"
            pagination={{
              current: page,
              pageSize,
              total: totalCount,
              showSizeChanger: false,
              onChange: setPage,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}

      <Drawer
        title={
          <Title level={4} className="!mb-0">
            {editing ? t('edit') : t('createTitle')}
          </Title>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={420}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="number"
            label={t('number')}
            rules={[
              { required: true, message: t('numberRequired') },
              {
                pattern: /^\S+$/,
                message: t('noSpaces'),
              },
              { max: 50 },
            ]}
          >
            <Input placeholder={t('enterNumber')} />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ max: 80 }]}>
            <Input placeholder={t('enterName')} />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>{t('cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={creating || updating}>
              {t('save')}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        title={<Title level={4} className="!mb-0">{t('deleteTitle')}</Title>}
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={() =>
          deleteFactory({ id: deleteTarget?._id, operatorId })
        }
        okText={t('delete')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true, loading: deleting }}
      >
        <p>{t('deleteConfirm')}</p>
        {deleteTarget?.number ? <p><strong>{deleteTarget.number}</strong></p> : null}
      </Modal>
    </div>
  );
}
